import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { FileText, Save, Edit2, X, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const ContentManager = ({ onUpdate }) => {
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [formData, setFormData] = useState({
    key: '',
    title_en: '',
    title_ti: '',
    content_en: '',
    content_ti: ''
  });

  const contentTypes = [
    { key: 'history', label: 'Church History', icon: '📜' },
    { key: 'purpose', label: 'Our Purpose', icon: '🎯' },
    { key: 'vision', label: 'Our Vision', icon: '🔮' },
    { key: 'youth', label: 'Youth Ministry', icon: '👶' },
    { key: 'building', label: 'Building Campaign', icon: '🏗️' },
    { key: 'custom', label: 'Add New Content', icon: '➕' }
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await api.get('/content');
      setContentItems(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      key: item.key,
      title_en: item.title_en,
      title_ti: item.title_ti,
      content_en: item.content_en,
      content_ti: item.content_ti
    });
    setEditingId(item.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/content/${editingId}`, formData);
        toast.success('Content updated successfully!');
      } else {
        await api.post('/admin/content', formData);
        toast.success('Content created successfully!');
      }
      resetForm();
      fetchContent();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    try {
      await api.delete(`/admin/content/${id}`);
      toast.success('Content deleted');
      fetchContent();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const resetForm = () => {
    setFormData({
      key: '',
      title_en: '',
      title_ti: '',
      content_en: '',
      content_ti: ''
    });
    setEditingId(null);
    setShowNewForm(false);
  };

  const getContentByKey = (key) => {
    return contentItems.find(item => item.key === key);
  };

  const handleCreateNew = () => {
    setFormData({
      key: '',
      title_en: '',
      title_ti: '',
      content_en: '',
      content_ti: ''
    });
    setShowNewForm(true);
    setEditingId(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Content Management</h2>
        <div className="flex space-x-2">
          {(editingId || showNewForm) && (
            <Button onClick={resetForm} variant="outline">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          )}
          {!editingId && !showNewForm && (
            <Button onClick={handleCreateNew} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Create New Content
            </Button>
          )}
        </div>
      </div>

      {/* Edit/Create Form */}
      {(editingId || showNewForm) && (
        <Card className="p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">
            {editingId ? `Editing: ${formData.title_en}` : 'Create New Content'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            {showNewForm && (
              <div>
                <label className="block text-sm font-medium mb-2">Content Key (Unique ID)</label>
                <Input
                  value={formData.key}
                  onChange={(e) => setFormData({...formData, key: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                  required
                  placeholder="e.g., history, services, events"
                  pattern="[a-z0-9-]+"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use lowercase letters, numbers, and hyphens only. This identifies the content (e.g., "history", "events")
                </p>
              </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title (English)</label>
                <Input
                  value={formData.title_en}
                  onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title (Tigrinya)</label>
                <Input
                  value={formData.title_ti}
                  onChange={(e) => setFormData({...formData, title_ti: e.target.value})}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Content (English)</label>
              <textarea
                className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600 font-mono text-sm"
                value={formData.content_en}
                onChange={(e) => setFormData({...formData, content_en: e.target.value})}
                required
                rows="12"
                placeholder="Enter content in English..."
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.content_en.length} characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Content (Tigrinya)</label>
              <textarea
                className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600 font-mono text-sm"
                value={formData.content_ti}
                onChange={(e) => setFormData({...formData, content_ti: e.target.value})}
                required
                rows="12"
                placeholder="ትግርኛ ትሕዝቶ አእትዉ..."
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.content_ti.length} characters
              </p>
            </div>

            <div className="flex space-x-3">
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Save Changes' : 'Create Content'}
              </Button>
              <Button type="button" onClick={resetForm} variant="outline">
                Cancel
              </Button>
              {editingId && (
                <Button 
                  type="button" 
                  onClick={() => handleDelete(editingId)} 
                  className="bg-red-600 hover:bg-red-700 ml-auto"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
          </form>
        </Card>
      )}

      {/* Content Items Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {loading ? (
          <div className="col-span-2 text-center py-8">Loading...</div>
        ) : (
          contentTypes.map((type) => {
            const content = getContentByKey(type.key);
            return (
              <Card key={type.key} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">{type.icon}</span>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {type.label}
                      </h3>
                      <p className="text-sm text-gray-500">Key: {type.key}</p>
                    </div>
                  </div>
                  {content ? (
                    <Button
                      onClick={() => handleEdit(content)}
                      size="sm"
                      variant="outline"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  ) : (
                    <span className="text-sm text-red-600">Not created</span>
                  )}
                </div>

                {content ? (
                  <div>
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {content.title_en}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {content.title_ti}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-3">
                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-4">
                        {content.content_en}
                      </p>
                    </div>
                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <span>
                        Updated: {new Date(content.updated_at).toLocaleDateString()}
                      </span>
                      <span>
                        {content.content_en.length + content.content_ti.length} chars total
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No content available</p>
                    <p className="text-xs">Click to create</p>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* Tips Section */}
      <Card className="p-6 mt-6 bg-blue-50 dark:bg-blue-900/20">
        <h4 className="font-bold text-blue-900 dark:text-blue-300 mb-3">
          💡 Content Editing Tips
        </h4>
        <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-400">
          <li>• Use line breaks to organize your content into paragraphs</li>
          <li>• Keep both English and Tigrinya versions synchronized</li>
          <li>• Changes appear immediately on the public website</li>
          <li>• Use bullet points with • or numbered lists for better readability</li>
          <li>• Preview changes on the About page after saving</li>
        </ul>
      </Card>
    </div>
  );
};

export default ContentManager;
